import {CreatorError, type Env} from './creatorDomain.ts';
export const CREATOR_WRITE_LIMITS = {daily: 20, monthly: 200} as const;
export const CREATOR_OPERATIONS = [
  {provider: 'higgsfield', action: 'generate_video', fields: ['prompt','model','duration','aspect_ratio'], required: ['prompt']},
  {provider: 'buffer', action: 'publish_post', fields: ['list_id','channel_id','body_text','mode','due_at','media_url','media_type'], required: ['list_id','channel_id','body_text','mode']},
] as const;
export const isCreatorOperation = (provider: unknown, action: unknown) => CREATOR_OPERATIONS.some(s => s.provider === provider && s.action === action);
export const creatorOperationsEnabled = (env: Env) => env('CREATOR_OPERATIONS_ENABLED') === 'true';
export function publicMediaUrl(raw: unknown): string {
  if (typeof raw !== 'string' || raw.length > 4096) throw new CreatorError('invalid_media_url');
  let u: URL; try { u = new URL(raw); } catch { throw new CreatorError('invalid_media_url'); }
  if (u.protocol !== 'https:' || u.username || u.password || u.port || !u.hostname.includes('.') ||
    /^(\d[\d.]*|\[.*\])$/.test(u.hostname) || /(^|\.)(localhost|local|internal|test|invalid)$/.test(u.hostname)) throw new CreatorError('invalid_media_url');
  return u.href;
}
export function validateCreatorOperation(b: Record<string, unknown>, prepared = false) {
  const spec = CREATOR_OPERATIONS.find(s => s.provider === b.provider && s.action === b.action);
  if (!spec) throw new CreatorError('operation_not_allowed', 403);
  const extra = prepared ? ['quote_id','account_id','account_name','destination_name','credits','available_credits','expires_at','idempotency_key'] : ['idempotency_key'];
  if (Object.keys(b).some(k => !['provider','action',...spec.fields,...extra].includes(k))) throw new CreatorError('invalid_request');
  const text = (key: string, max = 3000, required = false) => {
    const v = b[key];
    if (v === undefined && !required) return undefined;
    if (typeof v !== 'string' || !v.trim() || v.length > max || /\u0000/.test(v)) throw new CreatorError('invalid_' + key);
    return v.trim();
  };
  const out: Record<string, unknown> = {provider:b.provider,action:b.action};
  if (spec.provider === 'higgsfield') {
    out.prompt = text('prompt', 3000, true);
    out.model = text('model',80) ?? 'seedance_2_5';
    if (!['seedance_2_5','kling3_0','minimax_h3'].includes(String(out.model))) throw new CreatorError('invalid_model');
    out.duration = text('duration',2) ?? '5';
    if (!['5','10'].includes(String(out.duration))) throw new CreatorError('invalid_duration');
    out.aspect_ratio = text('aspect_ratio',5) ?? '9:16';
    if (!['9:16','16:9'].includes(String(out.aspect_ratio))) throw new CreatorError('invalid_aspect_ratio');
  } else {
    for (const key of ['list_id','channel_id']) {
      const v = text(key,160,true)!;
      if (!/^[A-Za-z0-9_-]+$/.test(v)) throw new CreatorError('invalid_' + key);
      out[key] = v;
    }
    out.body_text = text('body_text',10000,true);
    out.mode = text('mode',30,true);
    if (!['addToQueue','customScheduled','shareNow'].includes(String(out.mode))) throw new CreatorError('invalid_mode');
    if (out.mode === 'customScheduled') {
      const date = text('due_at',40,true)!;
      if (!/^\d{4}-\d\d-\d\dT\d\d:\d\d(?::\d\d(?:\.\d{3})?)?(Z|[+-]\d\d:\d\d)$/.test(date) || !Number.isFinite(Date.parse(date))) throw new CreatorError('invalid_due_at');
      out.due_at = new Date(date).toISOString();
    } else if (b.due_at) throw new CreatorError('invalid_due_at');
    if (b.media_url) {
      out.media_url = publicMediaUrl(b.media_url);
      out.media_type = text('media_type',10,true);
      if (!['image','video'].includes(String(out.media_type))) throw new CreatorError('invalid_media_type');
    } else if (b.media_type) throw new CreatorError('invalid_media_url');
  }
  if (b.idempotency_key !== undefined) {
    if (typeof b.idempotency_key !== 'string' || !/^plan:[0-9a-f-]{36}$/i.test(b.idempotency_key)) throw new CreatorError('invalid_action_id');
    out.idempotency_key = b.idempotency_key;
  }
  if (prepared) {
    if (typeof b.quote_id !== 'string' || !/^[0-9a-f-]{36}$/i.test(b.quote_id)) throw new CreatorError('quote_required',409);
    for (const key of ['quote_id','account_id','account_name','destination_name','credits','available_credits','expires_at']) if (b[key] !== undefined) out[key] = b[key];
  }
  return out;
}
export function videoParams(b: Record<string, unknown>) {
  return {model:b.model, prompt:b.prompt, duration:Number(b.duration), aspect_ratio:b.aspect_ratio, count:1, use_unlim:false};
}
// A missing or ambiguous price is never permission to submit a paid job.
export function videoCost(data: any): number {
  if (!data || data.error || data.unlim_choice || data.recovery_tool || (Array.isArray(data.adjustments) && data.adjustments.length)) throw new CreatorError('video_options_changed',409);
  // Native OAuth MCP returns cost: {credits, credits_exact}. If the provider
  // rounds a displayed price, approve the larger bound so we never underquote.
  const raw = data.cost ?? data.credits ?? data.total_cost;
  const amounts = raw && typeof raw === 'object' && !Array.isArray(raw)
    ? [raw.credits, raw.credits_exact].filter(v => v !== undefined)
    : [raw];
  if (!amounts.length || amounts.some(v => typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > 100000)) throw new CreatorError('cost_unavailable',502);
  return Math.max(...amounts);
}
